import React, { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { EnumType } from 'json-to-graphql-query'
import slugify from 'react-slugify'
import classNames from 'classnames'

import CustomMultiSelect from '../../../Input/CustomMultiSelect'
import InfoBox from '../../../InfoBox/InfoBox'
import AskVisoActions from '../layout/AskVisoActions'
import { Select } from '../../../../containers/CustomDashboardv2/Dashboard/details/editor/inputs/Select'
import { defaultNotifier } from 'functions/notificationHandler'
import { TIME_WINDOW_OPTIONS, UNITS_OPTIONS, CURRENCY_OPTIONS, DECIMALS_OPTIONS, RESPONSE_STYLE_OPTIONS, TONE_OPTIONS, FORMATTING_OPTIONS } from '../../constants'
import { updateAgentSettings } from '../../../../api/ai-agents'
import { handleMutation } from '../../../../utils/request'
import './AskVisoSettings.scss'

const AskVisoSettings = ({ color, locationsData, devicesData, settingsData, setSettingsData, setActiveTab, isFullscreen, workspace }) => {
  const { dataContext, responseStyle, id, locations } = settingsData
  const { timeWindow, devices } = dataContext
  const { detailLevel, tone, formatting } = responseStyle
  const [settingsLoading, toggleSettingsLoading] = useState(false)
  const [isTouched, toggleIsTouched] = useState(false)
  const [formValues, setFormValues] = useState({
    timeWindow: TIME_WINDOW_OPTIONS.find(option => option.apiValue === timeWindow?.value) || TIME_WINDOW_OPTIONS.find(opt => opt.label === 'Last 30 days') || TIME_WINDOW_OPTIONS[0],
    locations: locations ? locations.map(location => location.id) : [],
    devices: [...devices],
    detailLevel,
    tone,
    formatting
  })

  const handleFormatSelection = (value) => {
    toggleIsTouched(true)
    if (value.value === 'default') {
      setFormValues({ ...formValues, formatting: {} })
    } else {
      setFormValues({
        ...formValues,
        formatting: {
          unit: formatting.unit || 'metric',
          currency: formatting.currency || 'USD',
          decimals: formatting.decimals || 'none'
        }
      })
    }
  }

  const handleChange = (value) => {
    toggleIsTouched(true)
    if (Object.prototype.hasOwnProperty.call(value, 'timeWindow')) {
      const tw = value.timeWindow
      let normalized = tw
      const hasStartEnd = tw && tw.start && tw.end
      const looksRelative = !!(tw && ((tw.label && (/\blast\b/i.test(tw.label) || /^all$/i.test(tw.label))) || tw.apiValue === 'none'))
      if (hasStartEnd && !looksRelative) {
        setFormValues({ ...formValues, timeWindow: { ...tw, type: 'absolute' } })
        return
      }
      if (!tw?.apiValue) {
        if (tw?.value) {
          normalized = TIME_WINDOW_OPTIONS.find(opt => opt.apiValue === tw.value) || { ...tw, apiValue: tw.value, label: tw.label || '' }
        } else if (tw?.label) {
          normalized = TIME_WINDOW_OPTIONS.find(opt => opt.label === tw.label) || tw
        } else if (typeof tw?.totalDays !== 'undefined') {
          normalized = TIME_WINDOW_OPTIONS.find(opt => opt.totalDays === tw.totalDays) || tw
        }
      }
      const relativeSelected = normalized?.apiValue ? { ...normalized, type: 'relative', value: normalized.apiValue } : { ...normalized, type: 'relative' }
      setFormValues({ ...formValues, timeWindow: relativeSelected })
      return
    }
    setFormValues({ ...formValues, ...value })
  }

  const handleSubmit = async () => {
    toggleSettingsLoading(true)
    const deviceIds = formValues.devices.length > 0 ? formValues.devices : []
    const enumValue = (() => {
      const tw = formValues.timeWindow
      if (!tw) return 'last_30d'
      if (typeof tw.totalDays !== 'undefined') {
        const match = TIME_WINDOW_OPTIONS.find(opt => opt.totalDays === tw.totalDays)
        if (match?.apiValue) return match.apiValue
      }
      if (tw.apiValue) return tw.apiValue
      if (tw.value) return tw.value
      return 'last_30d'
    })()
    const twType = formValues.timeWindow?.type
    const hasStartEnd = formValues.timeWindow && formValues.timeWindow.start && formValues.timeWindow.end
    const labelLooksRelative = !!(formValues.timeWindow && formValues.timeWindow.label && /\blast\b/i.test(formValues.timeWindow.label))
    const isAbsolute = twType === 'absolute' || (!twType && hasStartEnd && !labelLooksRelative)
    const payload = {
      dataContext: {
        timeWindow: isAbsolute ? {
          type: new EnumType('absolute'),
          start: formValues.timeWindow.start,
          end: formValues.timeWindow.end,
        } : {
          type: new EnumType('relative'),
          value: new EnumType(enumValue)
        },
        devices: deviceIds
      },
      responseStyle: {
        detailLevel: formValues.detailLevel,
        tone: new EnumType(formValues.tone),
        ...(!isEmpty(formValues.formatting) && {
          formatting: {
            unit: new EnumType(formValues.formatting.unit),
            currency: new EnumType(formValues.formatting.currency),
            decimals: formValues.formatting.decimals
          }
        })
      }
    }

    try {
      const onSuccess = (response) => {
        setSettingsData(response.data.updateAgentSettings)
      }
      const response = await updateAgentSettings({ id: id, ...payload })
      handleMutation(response, onSuccess)
      defaultNotifier('Settings updated successfully')
    } catch (error) {
      defaultNotifier(error)
    } finally {
      toggleSettingsLoading(false)
    }
  }

  return (
    <div className={classNames('ask-viso-settings', isFullscreen && 'ask-viso-settings--fullscreen')}>

      <div className='ask-viso-settings__title'>Data Context</div>
      <Select
        label='Time Window'
        withTimeRangeMenu
        timeRangeOverflow='close'
        timeRangeAlignment={isFullscreen ? 'start' : 'end'}
        selectedOption={formValues.timeWindow}
        withDashboardDefault={false}
        onChange={(value) => handleChange({ timeWindow: value })}
        options={TIME_WINDOW_OPTIONS}
      />
      <CustomMultiSelect
        value={formValues.locations}
        color={color}
        onChange={(value) => handleChange({ locations: value })}
        options={locationsData}
        label='Locations'
        rectangularTags
        borderInput
        showAllOption='All'
      />
      <CustomMultiSelect
        value={formValues.devices.length === devicesData.length ? [] : formValues.devices}
        color={color}
        onChange={(value) => handleChange({ devices: value })}
        options={devicesData}
        label='Devices'
        rectangularTags
        borderInput
        showAllOption='All'
      />
      <div className='ask-viso-settings__title'>Response Style</div>
      <Select
        label='Detail Level'
        selectedOption={RESPONSE_STYLE_OPTIONS.find(option => option.value === formValues.detailLevel)}
        options={RESPONSE_STYLE_OPTIONS}
        onChange={(value) => handleChange({ detailLevel: value.value })}
      />
      <Select
        label='Tone'
        selectedOption={TONE_OPTIONS.find(option => option.value === formValues.tone)}
        options={TONE_OPTIONS}
        onChange={(value) => handleChange({ tone: value.value })}
      />
      <Select
        label='Formatting'
        selectedOption={!isEmpty(formValues.formatting) ? FORMATTING_OPTIONS[1] : FORMATTING_OPTIONS[0]}
        options={FORMATTING_OPTIONS}
        onChange={(value) => handleFormatSelection(value)}
      />
      {!isEmpty(formValues.formatting) &&
        <div className='border-left'>
          <Select
            label='Units'
            selectedOption={UNITS_OPTIONS.find(option => option.value === formValues.formatting.unit)}
            options={UNITS_OPTIONS}
            onChange={(value) => handleChange({ formatting: { ...formValues.formatting, unit: value.value } })}
          />
          <Select
            label='Currency'
            selectedOption={CURRENCY_OPTIONS.find(option => option.value === formValues.formatting.currency)}
            options={CURRENCY_OPTIONS}
            onChange={(value) => handleChange({ formatting: { ...formValues.formatting, currency: value.value } })}
          />
          <Select
            label='Decimals'
            selectedOption={DECIMALS_OPTIONS.find(option => option.value === formValues.formatting.decimals)}
            options={DECIMALS_OPTIONS}
            onChange={(value) => handleChange({ formatting: { ...formValues.formatting, decimals: value.value } })}
          />
        </div>
      }
      <div className='ask-viso-settings__note'>
        <InfoBox
          title='Note:'
          text={<>Ask Viso is referencing data from your <NavLink className={`viso-text-${color}`} to={`/${workspace && slugify(workspace.name)}/settings/ai-content-center`}>AI content center</NavLink>.</>}
          titleWeight='medium'
        />
      </div>
      <div className='ask-viso-settings__actions'>
        <AskVisoActions
          color={color}
          onSubmit={handleSubmit}
          submitLabel='Save'
          onCancel={() => setActiveTab('welcome')}
          submitDisabled={!isTouched || settingsLoading}
          cancelDisabled={!isTouched || settingsLoading}
        />
      </div>
    </div>
  )
}

export default AskVisoSettings